import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useSchemas } from "@/contexts/SchemaContext";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";

const LEVELS = [1, 2, 3, 4, 5];

// What every activity is for, what each difficulty level changes, and whether it can be used yet.
export default function ModuleGuide() {
  const { modules: mdata, patient: pdata } = useSchemas();
  const [links, setLinks] = useState({});
  const [area, setArea] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  useEffect(() => { api.get("/modules/overview").then((r) => setLinks(r.data.modules || {})).catch(() => {}); }, []);

  const areaLabel = useMemo(() => Object.fromEntries((pdata?.patientInputs || []).filter((g) => g.type === "group").map((g) => [g.key, g.label || g.key])), [pdata]);
  const areas = [...new Set((mdata?.modules || []).flatMap((m) => m.domains || []))];
  const list = (mdata?.modules || []).filter((m) => (!area || (m.domains || []).includes(area)) && (!onlyAvailable || (links[m.id]?.simulations || []).length > 0));

  return (
    <div>
      <PageHeader
        eyebrow="Reference"
        title="Activity guide"
        description="Every activity the platform offers: what it is for, what each difficulty level changes, and whether it is available on headsets yet."
      />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-xs text-slate-500 mr-1">Treatment area</span>
        {areas.map((a) => (
          <button key={a} type="button" onClick={() => setArea(area === a ? "" : a)} data-testid={`guide-area-${a}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${area === a ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-600 border-slate-200 hover:bg-purple-50"}`}>
            {areaLabel[a] || a}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} data-testid="guide-only-available" /> Available now only
        </label>
      </div>

      <div className="space-y-4">
        {list.map((m) => {
          const sims = links[m.id]?.simulations || [];
          const available = sims.length > 0;
          const rows = m.difficultyGuide || [];
          const custom = [...(m.customSettings || []), ...(m.setupOptions || [])];
          return (
            <details key={m.id} className="panel rounded-2xl group" data-testid={`guide-module-${m.id}`}>
              <summary className="cursor-pointer list-none p-5 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[16rem]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold text-slate-900">{m.name}</span>
                    {available
                      ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Available on headsets</Badge>
                      : <Badge variant="outline" className="bg-slate-100 text-slate-600 text-[10px]" data-testid={`coming-soon-${m.id}`}>Coming soon</Badge>}
                    {m.proposed && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]" title="Starting points to review. Not clinically validated yet.">Proposed settings</Badge>}
                    {(m.domains || []).map((d) => <Badge key={d} variant="outline" className="text-[10px]">{areaLabel[d] || d}</Badge>)}
                  </div>
                  <p className="text-sm text-slate-600 mt-1.5">{m.purpose || m.note}</p>
                </div>
                <span className="text-xs text-purple-700 group-open:hidden">Show levels</span>
                <span className="text-xs text-purple-700 hidden group-open:inline">Hide levels</span>
              </summary>
              <div className="px-5 pb-5 space-y-4">
                {rows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2">What each difficulty level changes</div>
                    <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
                      <thead className="bg-purple-50/60">
                        <tr><th className="text-left px-2 py-1.5 font-medium">Setting</th>{LEVELS.map((l) => <th key={l} className="text-left px-2 py-1.5 font-medium">Level {l}</th>)}</tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.label}>
                            <td className="px-2 py-1.5 border-t border-slate-100 font-medium">
                              {r.label}{(m.requiredLabels || []).includes(r.label) ? <span className="ml-1 text-[10px] text-purple-700" title="The patient must collect at least this many each round">(minimum per round)</span> : null}
                            </td>
                            {LEVELS.map((l, i) => <td key={l} className="px-2 py-1.5 border-t border-slate-100">{r.values[i]}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">{m.id === "calibration" ? "This is a setup activity: it has no difficulty levels." : "Level details have not been defined yet."}</p>
                )}
                {custom.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-1">{m.setupOptions ? "Setup options" : "Options you can set per patient"}</div>
                    <p className="text-xs text-slate-500 mb-2">{m.setupOptions ? "Chosen once when the activity is set up." : "These do not change with the level. You can also override any level setting for one patient (shown as \"Level 3 + custom\")."}</p>
                    <div className="flex flex-wrap gap-1.5">{custom.map((c) => <Badge key={c.Key} variant="outline" className="text-[11px] font-normal">{c.Label}</Badge>)}</div>
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  {available ? <>On headsets inside: {sims.map((s) => {
                    const others = (s.module_ids?.length ? s.module_ids : [s.module_id]).filter((x) => x && x !== m.id).map((x) => (mdata?.modules || []).find((y) => y.id === x)?.name || x);
                    return `${s.name}${s.version ? ` v${s.version}` : ""}${others.length ? ` (shared app, it also contains: ${others.join(", ")})` : ""}`;
                  }).join("; ")}.</>
                    : "No simulation is linked to this activity yet, so it cannot be assigned or used in a therapy."}
                </div>
              </div>
            </details>
          );
        })}
        {list.length === 0 && <div className="panel rounded-2xl p-10 text-center text-sm text-slate-500">No activities match.</div>}
      </div>
    </div>
  );
}
